import { useEffect, useState, useCallback } from 'react';

/**
 * Hook pour mesurer la hauteur d'un élément et se mettre à jour automatiquement
 * lors des redimensionnements, changements d'orientation, ou modifications de contenu.
 *
 * @param {React.RefObject<HTMLElement>} ref - Référence vers l'élément à mesurer
 * @param {Array} dependencies - Dépendances supplémentaires qui déclenchent une re-mesure
 * @returns {number} La hauteur de l'élément en pixels
 */
export function useElementHeight(ref, dependencies = []) {
    const [height, setHeight] = useState(0);

    const measureHeight = useCallback(() => {
        if (ref.current) {
            setHeight(ref.current.offsetHeight);
        }
    }, [ref]);

    useEffect(() => {
        if (!ref.current) return;

        // Mesure initiale
        measureHeight();

        // Observer les changements de taille de l'élément
        const resizeObserver = new ResizeObserver(measureHeight);
        resizeObserver.observe(ref.current);

        // Écouter le redimensionnement de la fenêtre et les changements d'orientation
        window.addEventListener('resize', measureHeight);
        window.addEventListener('orientationchange', measureHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', measureHeight);
            window.removeEventListener('orientationchange', measureHeight);
        };
    }, [ref, measureHeight, ...dependencies]);

    return height;
}
